'use client'

import { Card, Cell, Image, Spacer, Text } from '@vinted/web-ui'
import { ReactNode, useContext, useState } from 'react'
import { useIntl } from 'react-intl'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { useAsset } from '@marketplace-web/shared/assets'
import { ShippingDetailsContext } from '@marketplace-web/item-page/item-page-data'
import {
  clickEvent,
  itemPageViewEvent,
  EscrowFeesContext,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'

import ItemVerificationSellerModal from '../ItemVerificationSellerModal'
import ItemVerificationBuyerModal from '../ItemVerificationBuyerModal'
import { ItemPageOfflineVerificationBlockPluginModel } from './types'

type Props = {
  data: ItemPageOfflineVerificationBlockPluginModel
}

const ItemPageOfflineVerificationBlockPlugin = ({ data }: Props) => {
  const { user } = useSession()
  const { locale } = useIntl()
  const { shippingDetails } = useContext(ShippingDetailsContext)
  const { escrowFees } = useContext(EscrowFeesContext)

  const isViewingOwnItem = user?.id === data?.itemUserId
  const userSide = isViewingOwnItem ? 'seller' : 'buyer'

  const { track } = useTracking()
  const asset = useAsset('/assets/offline-authenticity')
  const translate = useTranslate(`item.authenticity_fee.${userSide}`)

  const [isBuyerModalOpen, setIsBuyerModalOpen] = useState(false)
  const [isSellerModalOpen, setIsSellerModalOpen] = useState(false)

  if (!shippingDetails) return null

  const getFormattedVerificationFee = () => {
    const finalPrice = escrowFees?.itemVerification?.finalPrice

    return (
      data.isOfflineVerificationAvailable && finalPrice && formatCurrencyAmount(finalPrice, locale)
    )
  }

  const formattedVerificationFee = getFormattedVerificationFee()

  if (!formattedVerificationFee) return null

  const verificationIconPath = asset('authenticity-diamond-48.svg', {
    theme: { dark: 'authenticity-diamond-48-dark.svg' },
  })

  const handleIsModalOpen = (isOpen: boolean) =>
    isViewingOwnItem ? setIsSellerModalOpen(isOpen) : setIsBuyerModalOpen(isOpen)

  const handleClick = () => {
    handleIsModalOpen(true)

    const target = isViewingOwnItem ? 'physical_auth_seller' : 'physical_auth_buyer'

    track(clickEvent({ target, targetDetails: JSON.stringify({ position: 'bottom' }) }))
  }

  const handleClose = () => {
    handleIsModalOpen(false)
  }

  const howItWorksLink = {
    'how-it-works': (chunks: Array<ReactNode>) => (
      <span>
        <button type="button" onClick={handleClick}>
          <Text
            as="span"
            clickable
            text={chunks}
            type={Text.Type.Subtitle}
            testId="item-offline-verification-block-how-it-works-link"
          />
        </button>
      </span>
    ),
  }

  const renderContent = () => (
    <>
      <Spacer />
      <Cell
        styling={Cell.Styling.Tight}
        prefix={
          <Image
            src={verificationIconPath}
            size={Image.Size.Medium}
            testId="item-offline-verification-block-icon"
            aria={{
              'aria-hidden': 'true',
            }}
          />
        }
        title={
          <Text
            as="h2"
            text={translate('title')}
            theme="amplified"
            type={Text.Type.Title}
            testId="item-offline-verification-block-title"
          />
        }
        body={
          <>
            {!isViewingOwnItem && (
              <>
                <Text
                  as="h3"
                  text={translate('fee', { amount: formattedVerificationFee })}
                  theme="inherit"
                  type={Text.Type.Subtitle}
                  testId="item-offline-verification-block-amount"
                />
                <Spacer />
              </>
            )}
            <Text
              as="h3"
              text={translate('body', howItWorksLink)}
              theme="inherit"
              type={Text.Type.Subtitle}
              testId="item-offline-verification-block-description"
              html
            />
          </>
        }
      />
      <ItemVerificationBuyerModal
        show={isBuyerModalOpen}
        onClose={handleClose}
        verificationFee={escrowFees?.itemVerification?.finalPrice}
        viewEvent={itemPageViewEvent({
          item_id: data.itemId.toString(),
          target: 'physical_auth_buyer',
        })}
      />
      <ItemVerificationSellerModal
        show={isSellerModalOpen}
        onClose={handleClose}
        viewEvent={itemPageViewEvent({
          item_id: data.itemId.toString(),
          target: 'physical_auth_seller',
        })}
      />
    </>
  )

  return (
    <Card>
      <div className="u-overflow-hidden">
        <Cell>{renderContent()}</Cell>
      </div>
    </Card>
  )
}

export default ItemPageOfflineVerificationBlockPlugin
